import useAuth from "../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import React from "react";
import {
  HiPhotograph,
  HiHome,
  HiKey,
  HiLogin,
  HiExclamation,
  HiChevronDown,
  HiOutlineCog,
} from "react-icons/hi";
import { CustomButton } from "../../atoms/CustomButton";
import { ConfigStatusItem } from "../../atoms/ConfigStatusItem";
import { useGetCheckEntity } from "../../../hooks/entity";
import { PiProhibitBold } from "react-icons/pi";

const EntityCheck = ({
  entity_identification,
}: {
  entity_identification: string;
}) => {
  const { t } = useTranslation();
  const { customer_identification } = useAuth();
  const navigate = useNavigate();

  const [expanded, setExpanded] = React.useState(false);
  const [showOptional, setShowOptional] = React.useState(false);

  const {
    isLoading,
    isError,
    isSuccess,
    data: res,
    refetch,
  } = useGetCheckEntity(customer_identification, entity_identification);

  const configItems = React.useMemo(() => {
    const serviceStatus =
      res?.entity_status && res.entity_status === "SERVICE_ON";

    return [
      {
        key: "service-status",
        icon: PiProhibitBold,
        label: t("dashboard.entityCheck.serviceStatusLabel"),
        status: serviceStatus ? "OK" : undefined,
        description: t("dashboard.entityCheck.serviceStatusDescription"),
        mandatory: true,
      },
      {
        key: "logo",
        icon: HiPhotograph,
        label: t("dashboard.entityCheck.text-01"),
        status: res?.cdn_logo_project_path,
        description: t("dashboard.entityCheck.text-01-desc"),
        mandatory: true,
      },
      {
        key: "email",
        icon: HiHome,
        label: t("dashboard.entityCheck.text-02"),
        status: res?.email_address_sender,
        description: t("dashboard.entityCheck.text-02-desc"),
        mandatory: true,
      },
      {
        key: "reset",
        icon: HiKey,
        label: t("dashboard.entityCheck.text-03"),
        status: res?.web_site_reset_password_page,
        description: t("dashboard.entityCheck.text-03-desc"),
        mandatory: false,
      },
      {
        key: "login",
        icon: HiLogin,
        label: t("dashboard.entityCheck.text-04"),
        status: res?.web_site_login_page,
        description: t("dashboard.entityCheck.text-04-desc"),
        mandatory: false,
      },
    ];
  }, [res, t]);

  const mandatoryItems = configItems.filter((item) => item.mandatory);
  const completedMandatoryItems = mandatoryItems.filter(
    (item) => item.status === "OK"
  ).length;
  const allMandatoryConfigurationsComplete =
    completedMandatoryItems === mandatoryItems.length;

  const optionalItems = configItems.filter((item) => !item.mandatory);

  const getNavigationSection = (key: string) => {
    switch (key) {
      case "service-status":
        return "input-service-status";
      case "logo":
        return "input-logo";
      case "email":
        return "emailSenderConfig";
      default:
        return "linksConfig";
    }
  };

  const renderConfigItem = (item: (typeof configItems)[0]) => {
    const isCompleted = item.status === "OK";

    return (
      <ConfigStatusItem
        key={item.key}
        icon={item.icon}
        label={item.label}
        description={item.description}
        completed={isCompleted}
        action={
          !isCompleted && (
            <CustomButton
              variant="outline"
              title={t("common.update")}
              onClick={() => {
                const sectionId = getNavigationSection(item.key);
                navigate(`/control-panel/settings#${sectionId}`, {
                  state: { section: sectionId },
                });
              }}
              size="sm"
              className="text-xs"
            />
          )
        }
      />
    );
  };

  if (isLoading) return null;
  if (isSuccess && allMandatoryConfigurationsComplete) return null;

  if (isError) {
    return (
      <div className="w-full bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
          <HiExclamation className="w-5 h-5 text-red-500" />
        </div>
        <p className="flex-1 text-sm text-gray-600 font-figtree">
          {t("dashboard.entityCheck.error")}
        </p>
        <CustomButton
          size="sm"
          onClick={() => refetch()}
          title={t("dashboard.entityCheck.btn-01") as string}
        />
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-lg border border-gray-200">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-4 text-left focus:outline-none"
        aria-expanded={expanded}
      >
        <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
          <HiOutlineCog className="w-5 h-5 text-amber-600" />
        </div>
        <span className="flex-1 text-sm font-semibold text-gray-900 font-figtree">
          {t("dashboard.entityCheck.compactTitle")}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold font-figtree flex-shrink-0">
          {t("dashboard.entityCheck.stepsDone", {
            done: completedMandatoryItems,
            total: mandatoryItems.length,
          })}
        </span>
        <HiChevronDown
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {mandatoryItems.map(renderConfigItem)}
          <button
            type="button"
            onClick={() => setShowOptional(!showOptional)}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 font-figtree transition-colors"
          >
            {t("dashboard.entityCheck.optionalToggle", {
              count: optionalItems.length,
            })}
            <HiChevronDown
              className={`w-3 h-3 transition-transform duration-200 ${
                showOptional ? "rotate-180" : ""
              }`}
            />
          </button>
          {showOptional && optionalItems.map(renderConfigItem)}
        </div>
      )}
    </div>
  );
};

export default EntityCheck;
